/**
 * Per-user and clinic-wide settings, stored server-side. Currently:
 *   - deepgramKey : Deepgram API key for dictation
 *   - reportHeader: JSON-stringified HeaderConfig
 *
 * Backend merges clinic-wide defaults (rows with NULL user_id) with per-user
 * overrides; per-user wins on read. Writes target one scope at a time.
 */
import { apiCall } from '../api/client';

export type SettingScope = 'clinic' | 'user';

export interface UserSettings {
  deepgramKey?: string;
  /** Deepgram API host — change per deployment region so each clinic hits
   *  its nearest POP. Default `api.in.deepgram.com` (Hyderabad, best for
   *  India). Middle-East clinics should set `api.deepgram.com` (global)
   *  for now; `api.eu.deepgram.com` once Deepgram EU is latency-tested
   *  from the ME. Full host, no scheme — scheme is always wss. */
  deepgramHost?: string;
  /** Deepgram streaming model. `nova-3-medical` is the current best for
   *  clinical dictation; `nova-2-medical` is the previous generation (and
   *  what the viewer used to hardcode). Users can set any model slug
   *  Deepgram exposes (e.g. `nova-3`, `base-medical`). */
  deepgramModel?: string;
  /** Deepgram `endpointing` parameter in milliseconds. Lower = snappier
   *  finalisation, but risks cutting a user off mid-sentence during
   *  natural pauses. 300ms is a good default for clinical dictation.
   *  Range Deepgram accepts: 10-5000. Set 0 to disable. */
  deepgramEndpointing?: number;

  /** Opt-in AI post-processing of each final transcript via an LLM to
   *  clean up punctuation + capitalization + minor transcription slips.
   *  OFF by default; adds ~200-400ms per utterance when ON. Groq's free
   *  tier is the recommended backend (sub-200ms Llama 3.3 70B). */
  dictationPostProcessEnabled?: boolean;
  /** OpenAI-compatible completions endpoint. Defaults to Groq's URL. */
  dictationPostProcessEndpoint?: string;
  /** Model slug on the endpoint. For Groq, `llama-3.3-70b-versatile` is
   *  a reasonable floor; punctuation cleanup doesn't need frontier
   *  intelligence. */
  dictationPostProcessModel?: string;
  /** API key for the post-process endpoint. For Groq: free from
   *  https://console.groq.com/keys. */
  dictationPostProcessKey?: string;
  reportHeader?: string;
  /** JSON-stringified Macro[] — report text snippets + voice aliases */
  reportMacros?: string;
  /** How the worklist opens studies:
   *   'modal'        — viewer renders as a full-window overlay over the
   *                    worklist; closing returns to the worklist without
   *                    losing any state. Esc closes too.
   *   'multi-tab'    — first study spawns a viewer tab; subsequent ones
   *                    pile into it via BroadcastChannel (multi-study tree).
   *   'separate-tab' — every Open Study spawns its own viewer tab (legacy). */
  viewerOpenMode?: 'modal' | 'multi-tab' | 'separate-tab';

  /** Data backend. EDGEPACS uses the paired image service over its
   *  proprietary REST API (current default); DICOMweb uses standard
   *  QIDO-RS / WADO-RS endpoints against any compliant server (Orthanc,
   *  dcm4chee, AWS HealthImaging, Google Healthcare API, etc.) */
  serverType?: 'edgepacs' | 'dicomweb';
  /** Base URL of the DICOMweb root — e.g. https://pacs.local/dicom-web.
   *  QIDO/WADO/STOW endpoints are derived from this. */
  dicomwebUrl?: string;
  /** Auth flavor for the DICOMweb server. Most installs use 'none' (relying
   *  on network ACL) or 'basic'; OAuth2-protected clouds use 'bearer'. */
  dicomwebAuth?: 'none' | 'basic' | 'bearer';
  /** Basic auth credentials. Stored as separate user/pass so the UI can
   *  show "currently configured" without round-tripping the secret. */
  dicomwebBasicUser?: string;
  dicomwebBasicPass?: string;
  /** Pre-issued bearer token (e.g. OAuth2 access token, or an API key the
   *  server expects in Authorization). */
  dicomwebBearerToken?: string;
  /** JSON-stringified PageFormat — user's preferred report page size and
   *  margins. Applied via Syncfusion's setDefaultSectionFormat() at editor
   *  boot AND baked into the header SFDT for new reports. Doesn't
   *  retroactively change already-saved reports (their section format is
   *  frozen in the SFDT payload) - matches how MS Word treats Normal.dot. */
  reportPageFormat?: string;

  /** JSON-stringified RuntimePluginConfig[] — installed vendor plugins
   *  (e.g., LIRA/Lenek, Aidoc, future HL7 forwarders). Each entry has a
   *  baseUrl, enabled flag, plugin-specific config, and per-plugin rules.
   *  See src/plugins/runtime/types.ts for the shape. Edited through
   *  Settings → AI Plugins; stored JSON-string here because UserSettings
   *  values are string on the server side. Clinic-wide scope (every reader
   *  in a practice sees the same installed plugins). */
  plugins?: string;

  /** VENDOR-WRITTEN allowlist of plugin ids this deployment is licensed
   *  for. Only plugins whose id appears here can be added via Settings
   *  → AI Plugins. Written by the installer / deploy script, not by the
   *  admin UI — treat as read-only in the clinic.
   *
   *  Format: comma-separated plugin ids ('lira-triage,manual-tagger').
   *  Empty / unset means NO plugins allowed — Settings → AI Plugins
   *  shows a "Contact your vendor to enable AI plugins" message.
   *
   *  Why not a hardcoded build-time constant? Because the same EdgeWeb
   *  bundle ships to every customer; per-customer builds would multiply
   *  release cadence. Server-side setting means one installer per
   *  customer sets their allowlist at deploy time. */
  allowedPluginIds?: string;
}

export type ViewerOpenMode = NonNullable<UserSettings['viewerOpenMode']>;
export const DEFAULT_VIEWER_OPEN_MODE: ViewerOpenMode = 'multi-tab';

/** Deepgram defaults. India regional endpoint since September 2026 cuts
 *  200-250ms off every round-trip for Indian clinics. Non-India customers
 *  (Middle East, elsewhere) override `deepgramHost` to `api.deepgram.com`
 *  (global) or whichever regional POP is nearest. */
export const DEFAULT_DEEPGRAM_HOST = 'api.in.deepgram.com';
export const DEFAULT_DEEPGRAM_MODEL = 'nova-3-medical';
export const DEFAULT_DEEPGRAM_ENDPOINTING = 300;

/** Groq's OpenAI-compatible completions endpoint. Sub-200ms inference
 *  with a generous free tier (sign up at console.groq.com).
 *  Default model: gpt-oss-20b — ~1000 tokens/sec, cheapest available
 *  ($0.075/$0.30 per 1M tokens), overkill for punctuation cleanup.
 *  Groq retired Llama 3.3 70B in late 2026; switching to the current
 *  production floor. */
export const DEFAULT_POSTPROCESS_ENDPOINT = 'https://api.groq.com/openai/v1/chat/completions';
export const DEFAULT_POSTPROCESS_MODEL = 'openai/gpt-oss-20b';

export type ServerType = NonNullable<UserSettings['serverType']>;
export const DEFAULT_SERVER_TYPE: ServerType = 'edgepacs';

export type DicomWebAuth = NonNullable<UserSettings['dicomwebAuth']>;

export interface SettingsSnapshot {
  values: UserSettings;
  /** Which scope each value came from — absent if the key wasn't set at all */
  scope: Partial<Record<keyof UserSettings, SettingScope>>;
}

let cache: SettingsSnapshot | null = null;
let inflight: Promise<SettingsSnapshot> | null = null;

export async function loadSettingsFull(force = false): Promise<SettingsSnapshot> {
  if (cache && !force) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const res = await apiCall<any>('settings.get', {});
    const row = res.s === 'ok' ? (res.data?.[0] ?? {}) : {};
    const { _scope, ...values } = row;
    const snapshot: SettingsSnapshot = { values, scope: _scope || {} };
    cache = snapshot;
    inflight = null;
    return snapshot;
  })();
  return inflight;
}

/** Back-compat convenience — returns just the merged values (clinic + user overrides). */
export async function loadUserSettings(force = false): Promise<UserSettings> {
  return (await loadSettingsFull(force)).values;
}

/** Save one or more settings to the specified scope (default: clinic). */
export async function saveUserSettings(
  settings: Partial<UserSettings>,
  scope: SettingScope = 'clinic'
): Promise<boolean> {
  const res = await apiCall('settings.save', { settings, scope });
  if (res.s === 'ok') {
    // Invalidate cache — next read re-fetches merged values
    cache = null;
    return true;
  }
  return false;
}

/** Remove a setting at the given scope. Deleting a 'user' override makes the
 *  clinic default visible again. */
export async function deleteUserSetting(
  key: keyof UserSettings,
  scope: SettingScope = 'user'
): Promise<boolean> {
  const res = await apiCall('settings.delete', { key, scope });
  if (res.s === 'ok') {
    cache = null;
    return true;
  }
  return false;
}

export function clearSettingsCache() {
  cache = null;
  inflight = null;
}
